<template>
  <div v-if="pageResponse && isSearchPage">
    <LazyLibSearchPage :is-locale-active="isLocaleActive" />
    <template v-if="isPageAccessibleForUser">
      <LibGroupedSections
        v-if="searchPageContentSections?.length"
        :entries="searchPageContentSections"
      />
      <LibGroupedSections
        v-if="searchPageCallToAction"
        :entries="[searchPageCallToAction]"
        :as-section="true"
      />
    </template>
  </div>
  <div v-else-if="pageResponse" :key="componentKey">
    <LibInactiveLocaleMessage :is-locale-active="isLocaleActive" />
    <LazyLibBreadcrumb
      v-if="showBreadcrumb"
      :trail="pageResponse.breadcrumbTrail"
    />
    <LazyIdleMappedComponent
      v-if="isPageAccessibleForUser"
      hydrate-on-idle
      :item="pageResponse.page"
      :binding="contentPageBinding"
    />

    <div
      v-else
      style="
        padding-top: 50px;
        gap: 50px;
        display: flex;
        flex-direction: column;
      "
    >
      <div
        :class="`hero-overview theme-${
          $colorMode === 'light' ? 'light' : 'dark'
        }`"
      >
        <LoadingSkeleton style="height: 300px; width: 100%" />
      </div>
      <template v-if="isAccountArea">
        <div
          class="section-box"
          style="gap: 50px; display: flex; flex-direction: column"
        >
          <LoadingSkeleton style="height: 400px; width: 60%; margin: auto" />
          <LoadingSkeleton style="height: 400px; width: 60%; margin: auto" />
        </div>
      </template>
      <template v-else>
        <div
          :class="`content-section content-section--color-${
            $colorMode === 'light' ? 'light' : 'dark'
          } theme-${$colorMode === 'light' ? 'light' : 'dark'}`"
        >
          <div
            class="content-section__inner"
            style="gap: 50px; display: flex; flex-direction: column"
          >
            <LoadingSkeleton style="height: 400px; width: 60%; margin: auto" />
            <LoadingSkeleton style="height: 400px; width: 60%; margin: auto" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useContentfulPagePayload } from '~/composables/useContentfulPagePayload';
import {
  CONTENTFUL_PAGE_FETCH_KEY,
  useContentfulPagePayloadError
} from '~/composables/useContentfulPage';
import { pageToDefaultThemeMap } from '~/utils/pageToDefaultThemeMap';
import { buildUrlString } from '~/utils/buildUrlString';
import {
  INJECTED_COLOR_MODE,
  IS_ACCOUNT_AREA,
  isAccountAreaPage
} from '~~/lib/constants';
import { LoadingSkeleton } from '@hypercodestudio/basler-components';
import { isValidWebAppLocale } from '~/utils/guards/isValidWebAppLocale';
import { isVisible } from '~/utils/entry/isVisible';
import { buildContentPageMappedBinding } from '~/utils/contentful/buildContentPageMappedBinding';
import { collapseContentCenterSlug } from '~/utils/contentful/collapseContentCenterSlug';
import { buildMissingContentfulPageError } from '~/utils/contentful/buildMissingContentfulPageError';
import type { ContentfulPageResponseModel } from '~~/server/api/contentful/index.get';
import type { MinifiedSecondaryNavigation } from '~/types/MinifiedSecondaryNavigation';

const { $ContentfulLivePreview } = useNuxtApp();

definePageMeta({
  validate: (route) => {
    const localeParam = route.params.locale;
    const locale = Array.isArray(localeParam) ? localeParam[0] : localeParam;
    return (
      typeof locale === 'string' && isValidWebAppLocale(locale.toLowerCase())
    );
  }
});

const locale = useRouteParamLocale();
const slug = useSlug();
const { loggedIn } = useShopAuth();

const { $colorMode, $globalPageSettings, $localeSlugMap } = useNuxtApp();
const secondaryNavigationState = useState<MinifiedSecondaryNavigation | null>(
  'secondaryNavigation'
);

const pageResponseRef = await useContentfulPagePayload();
const pageErrorRef = useContentfulPagePayloadError();

if (!pageResponseRef.value) {
  // Do not throw 404 when a client-side CMS redirect is in progress.
  // useContentfulPage sets resolvedPageResponse to null after calling
  // enhancedNavigateTo; the raw useFetch cache still holds the redirect payload.
  const rawData = useNuxtData<ContentfulPageResponseModel>(
    CONTENTFUL_PAGE_FETCH_KEY
  ).data.value;
  const missingPageError = buildMissingContentfulPageError(
    pageErrorRef.value,
    rawData
  );

  if (missingPageError) {
    throw createError(missingPageError);
  }
}

// Stay tied to shared useState payload (updates on client-side route changes).
// NOTE: value can become null mid-flight when navigating to a non-CMS route
// (syncFetch sets resolvedPageResponse=null before this component unmounts).
// All downstream computeds and watchers must guard against null.
const pageResponse = computed(() => pageResponseRef.value);

const contentPageBinding = computed(() =>
  pageResponse.value
    ? buildContentPageMappedBinding({
        page: pageResponse.value.page,
        tocItems: pageResponse.value.tocItems,
        readingTime: pageResponse.value.readingTime,
        pageTeaserMeta: pageResponse.value.pageTeaserMeta
      })
    : undefined
);

const isPageAccessibleForUser = computed(() =>
  pageResponse.value ? isVisible(pageResponse.value.page) : false
);

const isAccountArea = computed(() =>
  pageResponse.value ? isAccountAreaPage(pageResponse.value.page) : false
);

provide(IS_ACCOUNT_AREA, isAccountArea);

// Component key to force re-render only when needed (e.g., after login on protected pages).
// Document/software center detail URLs collapse to the root slug so modal navigation
// does not remount the full page (same rule as useContentfulPage effectiveSlug).
const componentKeySlug = computed(() =>
  collapseContentCenterSlug(
    slug.value,
    $globalPageSettings.value?.documentCenterPage?.slug ?? undefined,
    $globalPageSettings.value?.softwareCenterPage?.slug ?? undefined
  )
);

const componentKey = computed(() => {
  if (isPageAccessibleForUser.value) {
    return `${componentKeySlug.value}-${
      loggedIn.value ? 'logged-in' : 'logged-out'
    }`;
  }
  return componentKeySlug.value;
});

// search action should only be added to homepage
// @see https://developers.google.com/search/docs/appearance/structured-data/sitelinks-searchbox
if (slug.value === '' && $globalPageSettings.value?.searchResultsPage?.slug) {
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: buildUrlString(locale.value, '', undefined, undefined, true),
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: buildUrlString(
                locale.value,
                $globalPageSettings.value?.searchResultsPage?.slug ?? '',
                'q={search_term_string}',
                undefined,
                true
              )
            },
            'query-input': 'required name=search_term_string'
          }
        }),
        tagPriority: -1 // Wird als letztes eingefügt
      }
    ]
  });
}

const companyName = $globalPageSettings.value?.companyName ?? '';
if (pageResponseRef.value) {
  useMetadata(
    pageResponseRef.value.page,
    companyName,
    pageResponseRef.value.pageTeaserMeta
  );
}

const isSearchPage = computed(
  () => pageResponse.value?.page.fields?.pageLayout === 'Search'
);

const searchPageContentSections = computed(
  () => pageResponse.value?.page.fields?.contentSection
);

const searchPageCallToAction = computed(
  () => pageResponse.value?.page.fields?.callToAction
);

const isLocaleActive = computed(
  () =>
    pageResponse.value?.page.fields?.locales?.some(
      (enabledLocale) => enabledLocale.toLowerCase() === locale.value
    ) ?? true
);

const pageTheme = computed(() =>
  pageResponse.value
    ? (pageResponse.value.page.fields.pageTheme ??
      pageToDefaultThemeMap[pageResponse.value.page.sys.contentType.sys.id])
    : undefined
);

const secondaryNavigation = computed(
  () => pageResponse.value?.secondaryNavigation ?? null
);

const showBreadcrumb = computed(
  () => pageResponse.value?.page.fields?.showBreadcrumb ?? true
);

watch(
  pageTheme,
  () => {
    if (pageTheme.value === undefined) {
      return;
    }
    $colorMode.value = pageTheme.value;
    provide(INJECTED_COLOR_MODE, readonly(pageTheme));
  },
  { immediate: true }
);

watch(
  () => pageResponse.value?.localeSlugMap,
  () => {
    if (!pageResponse.value) {
      return;
    }
    $localeSlugMap.value = pageResponse.value.localeSlugMap;
    const {
      public: { isPreviewEnabled }
    } = useRuntimeConfig();
    if (isPreviewEnabled && $ContentfulLivePreview?.initialized) {
      $ContentfulLivePreview.openEntryInEditor({
        locale: 'de-DE',
        fieldId: 'internalName',
        entryId: pageResponse.value.page.sys.id
      });
    }
  },
  { immediate: true }
);

watch(
  secondaryNavigation,
  (value) => {
    secondaryNavigationState.value = value;
  },
  { immediate: true }
);
</script>
